import type { ReactNode } from "react";
import { Modal } from "antd";
import { X } from "lucide-react";
import styled from "styled-components";
import { antdModalPhoneSheet } from "@/shared/components/Modal/phoneSheet";
import { MOBILE_GUTTER, TAP_TARGET, mediaPhone } from "@/shared/theme/breakpoints";

const PURPLE = "#3a0ca3";
const ACCENT = "#6936dd";
const TEXT = "#303030";
const MUTED = "#707070";
const SURFACE = "#f3f5ff";
const DOCUMENT_SURFACE = "#ebeefd";
const DIVIDER = "#cdd4f6";
const DRAWER_TOP = 80;

/**
 * `modal`: centred dialog, used when the document is opened from a page.
 * `drawer`: bottom sheet, used when the document is opened from inside another
 * modal. Per the design it only closes from the X button, never on outside click.
 */
export type LegalDocumentModalVariant = "modal" | "drawer";

const Shell = styled(Modal)<{ $variant: LegalDocumentModalVariant }>`
	max-width: calc(100vw - 32px);
	padding-bottom: 0;

	.ant-modal-content {
		display: flex;
		flex-direction: column;
		height: ${({ $variant }) =>
			$variant === "drawer" ? `calc(100vh - ${DRAWER_TOP}px)` : "min(720px, calc(100vh - 48px))"};
		padding: 0;
		overflow: hidden;
		border-radius: ${({ $variant }) => ($variant === "drawer" ? "24px 24px 0 0" : "24px")};
		background: ${SURFACE};
		box-shadow: 0 12px 24px 0 rgba(21, 89, 165, 0.24);
		font-family: Poppins, sans-serif;
	}

	.ant-modal-body {
		display: flex;
		flex: 1;
		flex-direction: column;
		min-height: 0;
		padding: 0;
	}

	.ant-modal-close {
		top: 12px;
		right: 12px;
		width: 56px;
		height: 56px;
		border-radius: 64px;
		color: ${TEXT};

		&:hover {
			background: rgba(58, 12, 163, 0.06);
			color: ${TEXT};
		}
	}

	/* Phones: a bottom sheet with the standard 60px top gap, whichever variant was asked for. */
	${antdModalPhoneSheet}

	${mediaPhone`
		.ant-modal-close {
			top: 12px;
			right: 8px;
			width: ${TAP_TARGET}px;
			height: ${TAP_TARGET}px;
		}
	`}
`;

const Header = styled.header`
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 24px 32px;
	border-bottom: 1px solid ${DIVIDER};

	${mediaPhone`padding: 20px ${MOBILE_GUTTER}px 16px;`}
`;

const Title = styled.h2`
	margin: 0;
	padding-right: 48px;
	color: ${TEXT};
	font-size: 24px;
	font-weight: 600;
	line-height: normal;

	${mediaPhone`font-size: 18px;`}
`;

const DocumentInfo = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	column-gap: 16px;
	/* Space wrapped lines (narrow screens) so the badge doesn't sit on the text below. */
	row-gap: 8px;
	color: ${MUTED};
	font-size: 14px;
	line-height: 24px;
`;

const VersionBadge = styled.span`
	padding: 4px 12px;
	border-radius: 100px;
	background: ${ACCENT};
	color: ${SURFACE};
	font-size: 12px;
	font-weight: 500;
	line-height: normal;
	white-space: nowrap;
`;

const CompanyName = styled.span`
	font-weight: 600;
`;

const Dates = styled.dl`
	display: flex;
	flex-wrap: wrap;
	column-gap: 24px;
	margin: 0;
	font-size: 14px;
	line-height: 24px;

	div {
		display: flex;
		gap: 16px;
	}

	dt {
		color: ${TEXT};
		font-weight: 600;
	}

	dd {
		margin: 0;
		color: ${MUTED};
	}
`;

const ScrollArea = styled.div`
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	background: ${DOCUMENT_SURFACE};
	scrollbar-color: ${PURPLE} ${SURFACE};

	/* 8px thumb inset 12px from the right edge and the header/footer. */
	&::-webkit-scrollbar {
		width: 20px;
	}

	&::-webkit-scrollbar-track,
	&::-webkit-scrollbar-thumb {
		border-right: 12px solid transparent;
		border-radius: 999px;
		background-clip: padding-box;
	}

	&::-webkit-scrollbar-track {
		margin: 12px 0;
		background-color: ${SURFACE};
	}

	&::-webkit-scrollbar-thumb {
		min-height: 65px;
		background-color: ${PURPLE};
	}
`;

const Footer = styled.footer`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 16px 32px;
	border-top: 1px solid ${DIVIDER};

	${mediaPhone`padding: 12px ${MOBILE_GUTTER}px;`}
`;

export interface LegalDocumentMeta {
	title: string;
	/** Display label for the badge, e.g. "Version 1.1". */
	version: string;
	/** Upper-case entity or scope line shown next to the badge. */
	companyName: string;
	/** Optional UEN / address line. */
	companyLine?: string;
	effectiveDate: string;
	lastUpdated: string;
}

interface LegalDocumentModalProps {
	open: boolean;
	onClose: () => void;
	meta: LegalDocumentMeta;
	children: ReactNode;
	/** Action bar (e.g. Cancel + Agree). Omitted for read-only documents. */
	footer?: ReactNode;
	variant?: LegalDocumentModalVariant;
	zIndex?: number;
}

/** Shared Figma `modal_legal` shell for legal documents. */
export const LegalDocumentModal = ({
	open,
	onClose,
	meta,
	children,
	footer,
	variant = "modal",
	zIndex,
}: LegalDocumentModalProps) => {
	const isDrawer = variant === "drawer";

	return (
		<Shell
			$variant={variant}
			open={open}
			onCancel={onClose}
			footer={null}
			width={1080}
			centered={!isDrawer}
			style={isDrawer ? { top: DRAWER_TOP } : undefined}
			maskClosable={!isDrawer}
			closeIcon={<X size={24} strokeWidth={1.75} aria-hidden />}
			destroyOnHidden
			zIndex={zIndex}
		>
			<Header>
				<Title>{meta.title}</Title>
				<DocumentInfo>
					<VersionBadge>{meta.version}</VersionBadge>
					<CompanyName>{meta.companyName}</CompanyName>
					{meta.companyLine && <span>{meta.companyLine}</span>}
				</DocumentInfo>
				<Dates>
					<div>
						<dt>Effective date:</dt>
						<dd>{meta.effectiveDate}</dd>
					</div>
					<div>
						<dt>Last updated:</dt>
						<dd>{meta.lastUpdated}</dd>
					</div>
				</Dates>
			</Header>
			<ScrollArea tabIndex={0} role="region" aria-label={meta.title}>
				{children}
			</ScrollArea>
			{footer && <Footer>{footer}</Footer>}
		</Shell>
	);
};
